import React, {
  Component, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../provider/AuthProvider';
import { useInstalledApps } from '../../provider/InstalledAppsProvider';
import useTranslation from '../../hooks/useTranslation';
import TourOverlay, { isMobileViewport } from './TourOverlay';
import { findTarget, waitForTarget } from './waitForTarget';
import { findTourFor, pathInScope, readPageMarkers } from './match';
import {
  TOUR_STATUS, hasSeenTour, isTipsMuted, markTour, setTipsMuted,
} from './storage';

const AUTO_START_DELAY = 800;
const MODAL_RETRY_DELAY = 1500;
const MODAL_RETRIES = 5;

const noop = () => {};
const TourContext = createContext({
  startTour: noop, stopTour: noop, activeTourId: null, tourForPath: () => null, tours: [],
});

/** Safe outside the provider too (e.g. a Header rendered on a public page). */
export const useTour = () => useContext(TourContext);

const otherModalOpen = () => typeof document !== 'undefined'
  && !!document.querySelector('[role="dialog"][aria-modal="true"]:not([data-tour-card])');

class TourErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // A broken tour must never take the page down with it.
    console.error('[tour] overlay crashed, ending tour', error);
    this.props.onError();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * Runs product tours. Mount once inside the router, above every page.
 *
 * `tours` is a list of definitions (see src/tours). Each step targets an
 * element by its `data-tour` attribute; missing targets are waited for, then
 * skipped (optional steps) or shown as a centred card.
 */
export function TourProvider({ tours, welcomeTourId = 'welcome', children }) {
  const { userdetails } = useAuth();
  const installed = useInstalledApps() || {};
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = userdetails?.id;

  // session: { tour, index, skipped: Set<number>, target: Element|null, ready }
  const [session, setSession] = useState(null);
  const sessionRef = useRef(null);
  sessionRef.current = session;
  const runRef = useRef(0);
  const abortRef = useRef(null);
  const returnFocusRef = useRef(null);
  const leaveRef = useRef(null);
  // Tours that ended without being recorded (e.g. page never rendered its
  // anchors) — don't auto-retry them until the next full page load.
  const suppressedRef = useRef(new Set());
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  // Tabs that switch without a URL change mark their content with
  // data-tour-page; track which markers are on screen.
  const [pageKey, setPageKey] = useState(() => readPageMarkers().join('|'));
  const pageKeyRef = useRef(pageKey);
  pageKeyRef.current = pageKey;
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return undefined;
    let frame = 0;
    const read = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = readPageMarkers().join('|');
        setPageKey((prev) => (prev === next ? prev : next));
      });
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['data-tour-page'],
    });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const isAvailable = useCallback((tour) => {
    if (!tour) return false;
    if (tour.appId && installed.isAppInstalled && !installed.isAppInstalled(tour.appId)) return false;
    if (tour.requires && !tour.requires({ userdetails })) return false;
    return true;
  }, [installed, userdetails]);

  const runLeaveHook = () => {
    const leave = leaveRef.current;
    leaveRef.current = null;
    try { leave?.(); } catch { /* ignore */ }
  };

  const end = useCallback((status) => {
    const current = sessionRef.current;
    runRef.current += 1;
    abortRef.current?.abort();
    runLeaveHook();
    if (current) {
      if (status) markTour(userId, current.tour, status);
      else suppressedRef.current.add(current.tour.id);
    }
    setSession(null);
    const back = returnFocusRef.current;
    returnFocusRef.current = null;
    if (back?.isConnected) back.focus?.({ preventScroll: true });
  }, [userId]);

  const goTo = useCallback(async (tour, requested, direction, skipped, resolvedAny) => {
    const run = ++runRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let index = requested;
    // Walking back past the first usable step: stay on the earliest one.
    if (index < 0) return goTo(tour, 0, 1, skipped, resolvedAny);
    if (index >= tour.steps.length) {
      end(TOUR_STATUS.COMPLETED);
      return undefined;
    }

    const step = tour.steps[index];
    const mobile = isMobileViewport();
    const advance = (nextSkipped) => goTo(tour, index + (direction || 1), direction || 1, nextSkipped, resolvedAny);

    if (skipped.has(index) || (mobile && step.mobile === 'skip')) {
      return advance(new Set(skipped).add(index));
    }

    runLeaveHook();
    // Hide the card while the next target resolves; the scrim and the previous
    // spotlight stay put so moving between steps doesn't flash the page.
    setSession((prev) => ({
      tour, index, skipped, target: prev?.target ?? null, ready: false, shown: !!prev?.shown,
    }));

    if (step.route && !pathInScope(pathnameRef.current, step.route, true)) navigate(step.route);
    try {
      step.beforeShow?.({ isMobile: mobile });
      leaveRef.current = step.onLeave ? () => step.onLeave({ isMobile: mobile }) : null;
    } catch { /* a hook failing must not stall the tour */ }

    let target = null;
    if (step.target) {
      // Optional steps are usually absent for a reason (permissions, no data):
      // give up on them quickly so Next never feels stuck.
      const timeout = step.timeout ?? (step.optional && resolvedAny ? 1500 : 4000);
      target = await waitForTarget(step.target, { timeout, signal: controller.signal });
      if (run !== runRef.current) return undefined;
      if (!target) {
        if (step.optional) return advance(new Set(skipped).add(index));
        // The page never rendered its anchor: probably not the page the tour
        // was written for (permissions, redirect). End quietly, don't record.
        if (!resolvedAny) {
          end(null);
          return undefined;
        }
      }
    }
    if (run !== runRef.current) return undefined;

    if (target) {
      const r = target.getBoundingClientRect();
      const offscreen = r.top < 0 || r.left < 0 || r.bottom > window.innerHeight || r.right > window.innerWidth;
      if (offscreen) {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
      }
    }
    setSession({ tour, index, skipped, target, ready: true, shown: true });
    return undefined;
  }, [end, navigate]);

  const startTour = useCallback((tourId, { force = false } = {}) => {
    const tour = tours.find((x) => x.id === tourId);
    if (!tour || !isAvailable(tour)) return false;
    if (force) {
      setTipsMuted(userId, false);
      suppressedRef.current.delete(tour.id);
    }
    if (!returnFocusRef.current) returnFocusRef.current = document.activeElement;
    goTo(tour, 0, 1, new Set(), false);
    return true;
  }, [tours, isAvailable, userId, goTo]);

  const stopTour = useCallback(() => end(TOUR_STATUS.SKIPPED), [end]);

  /** The module/tab tour for what's on screen now (Help Center "Show me around"). */
  const tourForPath = useCallback((pathname) => {
    const tour = findTourFor(tours.filter((x) => x.id !== welcomeTourId), pathname, readPageMarkers());
    return isAvailable(tour) ? tour : null;
  }, [tours, welcomeTourId, isAvailable]);

  // Leaving the tour's page (browser back, a link in a toast…) ends it:
  // the user chose to go elsewhere, so don't drag them back.
  useEffect(() => {
    const current = sessionRef.current;
    if (!current?.ready) return;
    const step = current.tour.steps[current.index];
    const scope = step?.route || current.tour.scope;
    const exact = !step?.route && !!current.tour.exact;
    if (scope && !pathInScope(location.pathname, scope, exact)) {
      end(TOUR_STATUS.SKIPPED);
    }
  }, [location.pathname, end]);

  // Same for in-page tabs: the tab's marker went away (tab switched). Re-check
  // after a beat so a re-render that swaps the node doesn't end the tour.
  useEffect(() => {
    const page = sessionRef.current?.ready && sessionRef.current.tour.page;
    if (!page || pageKey.split('|').includes(page)) return undefined;
    const timer = setTimeout(() => {
      if (sessionRef.current?.tour.page === page && !readPageMarkers().includes(page)) {
        end(TOUR_STATUS.SKIPPED);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [pageKey, end]);

  // First-visit auto-start.
  useEffect(() => {
    if (session || !userId || installed.isAppsLoading) return undefined;
    const pathname = location.pathname;
    const pages = pageKey ? pageKey.split('|') : [];
    const tour = findTourFor(tours.filter((x) => x.autoStart !== false), pathname, pages);
    if (!tour || !isAvailable(tour) || suppressedRef.current.has(tour.id)) return undefined;
    if (hasSeenTour(userId, tour)) return undefined;
    if (tour.id !== welcomeTourId) {
      if (isTipsMuted(userId)) return undefined;
      const welcome = tours.find((x) => x.id === welcomeTourId);
      if (welcome && !hasSeenTour(userId, welcome)) return undefined;
    }

    let tries = 0;
    let timer = null;
    const attempt = () => {
      if (pathnameRef.current !== pathname || pageKeyRef.current !== pageKey || sessionRef.current) return;
      if (otherModalOpen()) {
        if (++tries <= MODAL_RETRIES) timer = setTimeout(attempt, MODAL_RETRY_DELAY);
        return;
      }
      startTour(tour.id);
    };
    timer = setTimeout(attempt, AUTO_START_DELAY);
    return () => clearTimeout(timer);
  }, [location.pathname, pageKey, session, userId, installed.isAppsLoading, tours, welcomeTourId, isAvailable, startTour]);

  // Abort any pending wait on unmount (logout, hot reload).
  useEffect(() => () => {
    runRef.current += 1;
    abortRef.current?.abort();
  }, []);

  const handlers = useMemo(() => {
    if (!session) return null;
    const { tour, index, skipped } = session;
    return {
      onNext: () => goTo(tour, index + 1, 1, skipped, true),
      onBack: () => goTo(tour, index - 1, -1, skipped, true),
      onSkip: () => end(TOUR_STATUS.SKIPPED),
      onMute: () => {
        setTipsMuted(userId, true);
        end(TOUR_STATUS.SKIPPED);
      },
      // The spotlit node was replaced or removed: resolve the same step again.
      onTargetLost: () => goTo(tour, index, 1, skipped, true),
    };
  }, [session, goTo, end, userId]);

  const value = useMemo(() => ({
    startTour, stopTour, tourForPath, tours, activeTourId: session?.tour.id ?? null,
  }), [startTour, stopTour, tourForPath, tours, session]);

  let overlay = null;
  if ((session?.ready || session?.shown) && handlers) {
    const { tour, index, skipped, target, ready } = session;
    const step = tour.steps[index];
    const isMobile = isMobileViewport();
    // Progress counts steps the user will actually see, so "2 of 4" never
    // jumps to "3 of 3": intro cards, steps already skipped, steps hidden on
    // mobile, and upcoming optional steps whose target isn't on the page
    // (and so will be skipped) are left out.
    const willShow = (s, i) => !skipped.has(i) && !(isMobile && s.mobile === 'skip')
      && !(i > index && s.optional && s.target && !s.route && !findTarget(s.target));
    const counted = tour.steps
      .map((s, i) => ({ s, i }))
      .filter(({ s, i }) => s.variant !== 'intro' && willShow(s, i));
    const position = counted.findIndex(({ i }) => i === index);
    const firstVisible = tour.steps.findIndex((s, i) => willShow(s, i));
    const isLast = !tour.steps.some((s, i) => i > index && willShow(s, i));

    overlay = (
      <TourErrorBoundary key={tour.id} onError={() => end(null)}>
        <TourOverlay
          step={step}
          target={target}
          pending={!ready}
          dir={dir}
          t={t}
          vars={{ firstname: userdetails?.firstname || '' }}
          progress={position >= 0 && counted.length > 1 ? { current: position + 1, total: counted.length } : null}
          // Back is hidden on the first counted step: going back from there
          // would only reopen the welcome card.
          isFirst={index === firstVisible || position <= 0}
          isLast={isLast}
          showMute={tour.id !== welcomeTourId && index === firstVisible}
          {...handlers}
        />
      </TourErrorBoundary>
    );
  }

  return (
    <TourContext.Provider value={value}>
      {children}
      {overlay}
    </TourContext.Provider>
  );
}

export default TourProvider;
